import React, { useState, useEffect } from 'react';
import { X, CheckCircle, AlertCircle, CreditCard, Lock } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import clientPaymentService from '../services/clientPaymentService';

// Initialiser Stripe
const stripePromise = loadStripe(
  import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || 
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ||
  process.env.REACT_APP_OMAD_STRIPE_PUBLISHABLE_KEY ||
  'pk_test_placeholder' // Clé de test par défaut pour éviter l'erreur
);

/**
 * Composant de formulaire de paiement Stripe
 */
const PaymentForm = ({ invoice, clientEmail, onSuccess, onError, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Créer le PaymentIntent
      const paymentIntent = await clientPaymentService.createPaymentIntent(invoice.id, clientEmail);
      
      // Confirmer le paiement avec Stripe
      const cardElement = elements.getElement(CardElement);
      const { error: stripeError, paymentIntent: confirmedPayment } = await stripe.confirmCardPayment(
        paymentIntent.client_secret,
        {
          payment_method: {
            card: cardElement,
            billing_details: {
              email: clientEmail,
            },
          },
        }
      );

      if (stripeError) {
        setError(stripeError.message);
        setLoading(false);
        return;
      }

      if (confirmedPayment.status === 'succeeded') {
        // Traiter le paiement réussi
        await clientPaymentService.processSuccessfulPayment(confirmedPayment.id);
        setSuccess(true);
        onSuccess(invoice, confirmedPayment);
      }

    } catch (error) {
      console.error('Erreur lors du paiement:', error);
      setError(error.message || 'Une erreur est survenue lors du paiement');
      onError(error);
    } finally {
      setLoading(false);
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        '::placeholder': {
          color: '#aab7c4',
        },
        padding: '10px 12px',
      },
      invalid: {
        color: '#9e2146',
      },
    },
    hidePostalCode: true,
  };

  if (success) {
    return (
      <div className="text-center py-8">
        <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
          Paiement réussi !
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          Votre paiement pour <strong>{invoice.productName}</strong> a été traité avec succès.
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Facture: {invoice.invoiceNumber}
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Informations de la facture */}
      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
        <div className="flex justify-between items-center mb-2">
          <span className="text-gray-600 dark:text-gray-400">Produit</span>
          <span className="font-medium text-gray-900 dark:text-white">
            {invoice.productName}
          </span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-gray-600 dark:text-gray-400">Montant</span>
          <span className="text-xl font-bold text-green-600 dark:text-green-400">
            {(invoice.amount / 100).toFixed(2)} €
          </span>
        </div>
      </div>

      {/* Informations de carte */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Informations de carte
        </label>
        <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-3 bg-white dark:bg-gray-800">
          <CardElement options={cardElementOptions} />
        </div>
        <div className="flex items-center mt-2 text-sm text-gray-500 dark:text-gray-400">
          <Lock className="w-4 h-4 mr-1" />
          <span>Paiement sécurisé par Stripe</span>
        </div>
      </div>

      {/* Message d'erreur */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
            <span className="text-red-700 dark:text-red-300">{error}</span>
          </div>
        </div>
      )}

      {/* Boutons d'action */}
      <div className="flex space-x-3">
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
        >
          Annuler
        </button>
        <button
          type="submit"
          disabled={!stripe || loading}
          className="flex-1 px-4 py-3 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center"
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Traitement...
            </>
          ) : (
            <>
              <CreditCard className="w-4 h-4 mr-2" />
              Payer {(invoice.amount / 100).toFixed(2)} €
            </>
          )}
        </button>
      </div>
    </form>
  );
};

/**
 * Modal de paiement principal
 */
const ClientPaymentModal = ({ isOpen, onClose, invoice, clientEmail, clientName, onSuccess, onError }) => {
  const [paymentStatus, setPaymentStatus] = useState('idle'); // idle, processing, success, error

  if (!isOpen) return null;

  const handleSuccess = (invoice, paymentIntent) => {
    setPaymentStatus('success');
    console.log('Paiement réussi:', { invoice, paymentIntent });

    // Appeler le callback externe après 1.5 secondes pour laisser l'utilisateur voir le message de succès
    setTimeout(() => {
      if (onSuccess) {
        onSuccess(invoice, paymentIntent);
      }
      handleClose();
    }, 1500);
  };

  const handleError = (error) => {
    setPaymentStatus('error');
    console.error('Erreur de paiement:', error);

    if (onError) {
      onError(error);
    }
  };

  const handleClose = () => {
    setPaymentStatus('idle');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Overlay */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={handleClose}
        />

        {/* Modal */}
        <div className="relative inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
          
          {/* Bouton de fermeture */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>

          {/* En-tête */}
          <div className="text-center sm:text-left mb-6">
            <div className="flex items-center justify-center sm:justify-start">
              <CreditCard className="w-8 h-8 text-blue-500 mr-3" />
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                Paiement sécurisé
              </h3>
            </div>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              {clientName ? `Paiement pour ${clientName}` : 'Finalisez votre paiement en toute sécurité'}
            </p>
          </div>

          {/* Contenu du paiement */}
          <Elements stripe={stripePromise}>
            <PaymentForm
              invoice={invoice}
              clientEmail={clientEmail}
              onSuccess={handleSuccess}
              onError={handleError}
              onCancel={handleClose}
            />
          </Elements>

          {/* Informations de sécurité */}
          <div className="mt-6 border-t border-gray-200 dark:border-gray-600 pt-4">
            <div className="flex items-center justify-center text-xs text-gray-500 dark:text-gray-400 space-x-4">
              <div className="flex items-center">
                <Lock className="w-3 h-3 mr-1" />
                <span>SSL Sécurisé</span>
              </div>
              <div className="flex items-center">
                <CreditCard className="w-3 h-3 mr-1" />
                <span>Stripe</span>
              </div>
              <div className="flex items-center">
                <CheckCircle className="w-3 h-3 mr-1" />
                <span>Garantie</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientPaymentModal;
